import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, NO_ACCESS_MESSAGE, hasAtLeastOneRequiredAction } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";

const STACK_MANAGED_STATE_ROLLBACK_ACCESS_TYPES = [
  Action.SpaceAdmin,
  Action.StackManagedStateRollback,
];

const STACK_LOCKED_BY_ANOTHER_USER_ERROR_MESSAGE =
  "You can't rollback state of a stack locked by another user";

const hasStateRollbackAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(STACK_MANAGED_STATE_ROLLBACK_ACCESS_TYPES, viewer, spaceId);

type StackForRollback = Pick<Stack, "spaceDetails" | "space" | "lockedBy" | "isLockedByTemplateDeployment">;

/**
 * Hook that returns function to check if current user can rollback managed state of a stack
 * User needs to be admin, have root space admin access, or have SpaceAdmin or StackManagedStateRollback action in the stack's space
 * Stacks created from a template deployment cannot have state rolled back.
 * @returns {(stack: StackForRollback) => AccessResult} Function that takes a stack and returns an AccessResult indicating if user can rollback managed state and a message if not
 */
const useCanManagedStateRollbackStack = (): ((stack: StackForRollback) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const hasStackAdminAccessFn = useHasStackAdminAccess();

  return useCallback(
    (stack) => {
      const templateDeploymentCheck = checkTemplateDeploymentAccess(stack);
      if (templateDeploymentCheck) {
        return templateDeploymentCheck;
      }

      const isLockedByMe = viewer.id === stack.lockedBy;
      if (stack.lockedBy && !isLockedByMe) {
        return {
          result: false,
          message: STACK_LOCKED_BY_ANOTHER_USER_ERROR_MESSAGE,
        };
      }

      if (viewer.admin) {
        return { result: true };
      }

      if (hasStackAdminAccessFn(stack).result) {
        return { result: true };
      }

      if (hasStateRollbackAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer, hasStackAdminAccessFn]
  );
};

export default useCanManagedStateRollbackStack;
